/**
 * API Configuration for mobile and desktop access
 * Automatically detects if running on mobile device and uses appropriate backend URL
 */

// Get the current hostname (localhost or network IP)
const getHostname = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  return 'localhost';
};

// Determine if we're accessing from a mobile device on the network
const isNetworkAccess = (): boolean => {
  const hostname = getHostname();
  // If not localhost, assume network access
  return hostname !== 'localhost' && hostname !== '127.0.0.1';
};

// Get the backend API URL (never use http or :8000 when page is HTTPS - mixed content is blocked)
export const getApiBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';
  const { protocol, port, hostname } = window.location;
  // CRITICAL: On HTTPS (e.g. https://jaalu.in) always same-origin. Else browser blocks request.
  if (protocol === 'https:') return '';
  // Production/same-host over HTTP: use relative paths (Nginx proxies /api/)
  if (port !== '3000') return '';
  // Local dev (port 3000) with proxy: relative paths
  if (!isNetworkAccess()) return '';
  // Mobile testing: dev server on :3000, backend on :8000 (HTTP only)
  return `http://${hostname}:8000`;
};

/** Auth/API paths: relative on HTTPS (avoids mixed content); otherwise use apiConfig base. */
export const getAuthApiUrl = (path: string): string => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return path.startsWith('/') ? path : `/${path}`;
  }
  const base = getApiBaseUrl();
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
};

// Helper function to make API calls with proper base URL
export const apiFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  // Merge headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
};

const apiConfig = {
  getApiBaseUrl,
  apiFetch,
  isNetworkAccess,
};

export default apiConfig;

